import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { LeadFormModal } from "./components/LeadFormModal";
import { Toaster } from "./components/ui/sonner";
import { 
  Phone, 
  MessageCircle, 
  Download, 
  MapPin, 
  Shield, 
  Car, 
  TreeDeciduous, 
  Users, 
  Zap, 
  Droplets, 
  Lamp, 
  Baby, 
  Footprints,
  Home,
  TrendingUp,
  IndianRupee,
  Building,
  Heart,
  CheckCircle,
  ArrowDown,
  Play,
  Navigation,
  Star,
  ChevronRight,
  ChevronLeft,
  Menu,
  X
} from "lucide-react";

const WHATSAPP_NUMBER = '919926159666';
const PHONE_NUMBER = '+919926159666';
const BROCHURE_URL = 'https://customer-assets.emergentagent.com/job_villa-awaits/artifacts/ze2k80un_Dream%2BHomes%2B2.pdf';
const MAP_URL = 'https://maps.app.goo.gl/yJZZTiFm2Sg49vEN9';

// Hero Background Image
const HERO_BG = 'https://images.pexels.com/photos/17174766/pexels-photo-17174766.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const FAMILY_IMG = 'https://images.pexels.com/photos/7641906/pexels-photo-7641906.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const VILLA_IMG = 'https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const COMMUNITY_IMG = 'https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const EVENING_IMG = 'https://images.pexels.com/photos/2102587/pexels-photo-2102587.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

// Floor Plan Data
const floorPlans = [
  {
    id: 1,
    name: 'Ground Floor',
    block: '3BHK Duplex Villa',
    image: 'https://customer-assets.emergentagent.com/job_villa-awaits/artifacts/ygt2mkt4_WhatsApp%20Image%202026-02-23%20at%202.49.04%20PM.jpeg',
    specs: {
      sft: '1650',
      type: '3BHK',
      carpetArea: '1210',
      balcony: '1',
      study: 'Yes',
      bedArea: '450',
      sbd: '2BT'
    }
  },
  {
    id: 2,
    name: 'First Floor',
    block: '3BHK Duplex Villa',
    image: 'https://customer-assets.emergentagent.com/job_villa-awaits/artifacts/g7fk64dp_WhatsApp%20Image%202026-02-23%20at%202.49.05%20PM.jpeg',
    specs: {
      sft: '1650',
      type: '3BHK',
      carpetArea: '1210',
      balcony: '1',
      study: 'Yes',
      bedArea: '450',
      sbd: '2BT'
    }
  }
];

// Gallery Images
const galleryImages = [
  {
    id: 1,
    src: 'https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    alt: 'Dream Homes 2 Villa Front View'
  },
  {
    id: 2,
    src: 'https://images.pexels.com/photos/17174766/pexels-photo-17174766.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    alt: 'Dream Homes 2 Aerial View'
  },
  {
    id: 3,
    src: 'https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    alt: 'Dream Homes 2 Community'
  },
  {
    id: 4,
    src: 'https://images.pexels.com/photos/2102587/pexels-photo-2102587.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    alt: 'Dream Homes 2 Evening View'
  },
  {
    id: 5,
    src: 'https://images.pexels.com/photos/7641906/pexels-photo-7641906.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    alt: 'Dream Homes 2 Living'
  }
];

// Gallery Section Component
const GallerySection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const getSlidePosition = (index) => {
    const diff = index - currentIndex;
    const total = galleryImages.length;
    
    // Normalize the difference for circular navigation
    let normalizedDiff = diff;
    if (diff > total / 2) normalizedDiff = diff - total;
    if (diff < -total / 2) normalizedDiff = diff + total;
    
    return normalizedDiff;
  };

  return (
    <section id="gallery" className="py-16 md:py-24 bg-beige-light section-animate overflow-hidden" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 
            className="text-4xl md:text-6xl font-bold text-olive-dark mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Gallery
          </h2>
          <div className="inline-block">
            <span className="text-orange-500 font-medium text-lg italic" style={{ fontFamily: "'Playfair Display', serif" }}>
              Dream Homes ||
            </span>
            <div className="h-0.5 bg-orange-400 mt-1 w-full"></div>
          </div>
        </div>

        {/* 3D Carousel */}
        <div className="relative h-[400px] md:h-[500px] flex items-center justify-center">
          {/* Left Arrow */}
          <button
            onClick={prevSlide}
            className="absolute left-0 md:left-4 z-20 w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-orange-400 flex items-center justify-center hover:bg-orange-50 transition-all group"
            data-testid="gallery-prev-btn"
          >
            <ChevronLeft size={24} className="text-olive group-hover:text-orange-500 transition-colors" />
          </button>

          {/* Right Arrow */}
          <button
            onClick={nextSlide}
            className="absolute right-0 md:right-4 z-20 w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-orange-400 flex items-center justify-center hover:bg-orange-50 transition-all group"
            data-testid="gallery-next-btn"
          >
            <ChevronRight size={24} className="text-olive group-hover:text-orange-500 transition-colors" />
          </button>

          {/* Images Container */}
          <div className="relative w-full h-full flex items-center justify-center" style={{ perspective: '1000px' }}>
            {galleryImages.map((image, index) => {
              const position = getSlidePosition(index);
              const isCenter = position === 0;
              const isVisible = Math.abs(position) <= 2;
              
              if (!isVisible) return null;

              // Calculate styles based on position
              const translateX = position * 280;
              const translateZ = isCenter ? 0 : -150 - Math.abs(position) * 50;
              const scale = isCenter ? 1 : 0.75 - Math.abs(position) * 0.1;
              const opacity = isCenter ? 1 : 0.6 - Math.abs(position) * 0.15;
              const zIndex = 10 - Math.abs(position);

              return (
                <div
                  key={image.id}
                  className="absolute transition-all duration-500 ease-out"
                  style={{
                    transform: `translateX(${translateX}px) translateZ(${translateZ}px) scale(${scale})`,
                    opacity: Math.max(opacity, 0.3),
                    zIndex,
                  }}
                >
                  <div 
                    className={`rounded-2xl overflow-hidden shadow-2xl ${isCenter ? 'ring-4 ring-olive/20' : ''}`}
                    style={{
                      width: isCenter ? '350px' : '280px',
                      height: isCenter ? '400px' : '320px',
                    }}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-8">
          {galleryImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex ? 'bg-orange-500 w-6' : 'bg-olive/30'
              }`}
              data-testid={`gallery-dot-${index}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Navigation Menu Items
const navItems = [
  { label: 'Home', href: '#hero' },
  { label: 'About', href: '#solution' },
  { label: 'Floor Plans', href: '#plans' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Location', href: '#location' },
  { label: 'Contact', href: '#contact' },
];

// Premium Navbar Component
const Navbar = ({ openModal }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Update active section based on scroll position
      const sections = navItems.map(item => item.href.replace('#', ''));
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const id = href.replace('#', '');
    const element = document.getElementById(id);
    if (element) {
      const navHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - navHeight,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Main Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white shadow-md py-3' 
            : 'bg-transparent py-5'
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a 
              href="#hero" 
              onClick={(e) => { e.preventDefault(); scrollToSection('#hero'); }}
              className="flex items-center"
              data-testid="navbar-logo"
            >
              <div
                className={`transition-all duration-300 rounded-lg ${
                  isScrolled ? '' : 'bg-white/90 shadow-sm px-2 py-1'
                }`}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_villa-awaits/artifacts/5j5lhumo_image.png"
                  alt="Dream Homes II"
                  className="h-10 md:h-12 w-auto"
                />
              </div>
            </a>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => { e.preventDefault(); scrollToSection(item.href); }}
                  className={`relative text-sm font-medium tracking-wide uppercase transition-colors duration-300 group ${
                    isScrolled ? 'text-olive-dark hover:text-olive' : 'text-white/90 hover:text-white'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  {item.label}
                  {/* Underline Animation */}
                  <span 
                    className={`absolute -bottom-1 left-0 h-0.5 bg-orange-500 transition-all duration-300 ${
                      activeSection === item.href.replace('#', '') ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </a>
              ))}
            </div>

            {/* Desktop CTA & Mobile Menu Button */}
            <div className="flex items-center gap-4">
              {/* Enquire Now Button - Desktop */}
              <button
                onClick={() => openModal('brochure', 'Enquire Now')}
                className={`hidden lg:flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 ${
                  isScrolled 
                    ? 'bg-olive text-white hover:bg-olive-dark hover:shadow-lg hover:-translate-y-0.5' 
                    : 'bg-white text-olive hover:bg-beige hover:shadow-lg hover:-translate-y-0.5'
                }`}
                data-testid="navbar-enquire-btn"
              >
                Enquire Now
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden p-2 rounded-lg transition-colors duration-300 ${
                  isScrolled ? 'text-olive-dark hover:bg-beige' : 'text-white hover:bg-white/10'
                }`}
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'visible' : 'invisible'
        }`}
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-olive-dark/50 transition-opacity duration-300 ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Menu Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl transition-transform duration-300 ease-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          data-testid="mobile-menu-panel"
        >
          {/* Close Button */}
          <div className="flex justify-end p-4">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 rounded-lg text-olive-dark hover:bg-beige transition-colors"
              data-testid="mobile-menu-close"
            >
              <X size={24} />
            </button>
          </div>

          {/* Menu Items */}
          <div className="px-6 py-4">
            <nav className="space-y-1">
              {navItems.map((item, index) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => { e.preventDefault(); scrollToSection(item.href); }}
                  className={`block py-4 text-lg font-medium text-olive-dark hover:text-olive border-b border-beige-dark transition-all duration-300`}
                  style={{ 
                    animationDelay: `${index * 50}ms`,
                    animation: isMobileMenuOpen ? 'fadeInRight 0.3s ease forwards' : 'none'
                  }}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            {/* Mobile CTA Button */}
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                openModal('brochure', 'Enquire Now');
              }}
              className="w-full mt-8 px-6 py-4 bg-olive text-white font-semibold rounded-full hover:bg-olive-dark transition-all duration-300 uppercase tracking-wider"
              data-testid="mobile-enquire-btn"
            >
              Enquire Now
            </button>

            {/* Contact Info */}
            <div className="mt-8 pt-6 border-t border-beige-dark">
              <a 
                href="tel:+919926159666" 
                className="flex items-center gap-3 text-olive-dark hover:text-olive transition-colors py-2"
              >
                <Phone size={18} />
                <span>+91 99261 59666</span>
              </a>
              <a 
                href="https://wa.me/919926159666" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-olive-dark hover:text-olive transition-colors py-2"
              >
                <MessageCircle size={18} />
                <span>WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Plans Section Component
const PlansSection = ({ openModal }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const plansPerView = 2;
  const totalSlides = Math.ceil(floorPlans.length / plansPerView);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const visiblePlans = floorPlans.slice(
    currentSlide * plansPerView,
    currentSlide * plansPerView + plansPerView
  );

  return (
    <section id="plans" className="py-16 md:py-24 bg-beige section-animate" data-testid="plans-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-6xl font-poppins font-bold text-olive-dark mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Plans
          </h2>
          <div className="inline-block">
            <span className="text-olive font-medium text-lg">Floor Plan</span>
            <div className="h-0.5 bg-orange-400 mt-1 w-full"></div>
          </div>
        </div>

        {/* Floor Plans Carousel */}
        <div className="relative">
          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-0 md:-left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full border-2 border-orange-400 flex items-center justify-center hover:bg-orange-50 transition-all group"
            data-testid="plans-prev-btn"
          >
            <ChevronLeft size={24} className="text-olive group-hover:text-orange-500 transition-colors" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-0 md:-right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full border-2 border-orange-400 flex items-center justify-center hover:bg-orange-50 transition-all group"
            data-testid="plans-next-btn"
          >
            <ChevronRight size={24} className="text-olive group-hover:text-orange-500 transition-colors" />
          </button>

          {/* Plans Grid */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 px-8 md:px-16">
            {visiblePlans.map((plan) => (
              <div
                key={plan.id}
                className="bg-beige-light rounded-lg shadow-sm overflow-hidden transition-all hover:shadow-md"
                data-testid={`floor-plan-card-${plan.id}`}
              >
                {/* Plan Header */}
                <div className="p-4 border-b border-beige-dark">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-olive font-semibold text-sm uppercase tracking-wide">{plan.name}</p>
                      <p className="text-olive/60 text-xs">{plan.block}</p>
                    </div>
                    {/* Floor Position Indicator */}
                    <div className="flex flex-col items-center">
                      <svg width="60" height="50" viewBox="0 0 60 50" className="text-olive/40">
                        {/* Simple building floor indicator */}
                        <rect x="15" y="5" width="30" height="40" fill="none" stroke="currentColor" strokeWidth="1"/>
                        <line x1="15" y1="25" x2="45" y2="25" stroke="currentColor" strokeWidth="1"/>
                        {/* Highlight floor based on plan */}
                        <rect x="15" y={plan.id === 1 ? "25" : "5"} width="30" height="20" fill="currentColor" fillOpacity="0.3"/>
                        {/* Floor labels */}
                        <text x="30" y="18" textAnchor="middle" fontSize="6" fill="currentColor">1F</text>
                        <text x="30" y="38" textAnchor="middle" fontSize="6" fill="currentColor">GF</text>
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Floor Plan Image */}
                <div className="p-4 bg-white flex items-center justify-center">
                  <img 
                    src={plan.image} 
                    alt={plan.name}
                    className="w-full h-auto max-h-[400px] object-contain rounded-lg"
                  />
                </div>

                {/* Specs Bar */}
                <div className="bg-red-600 text-white px-3 py-2">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <span className="text-white/70 text-[10px] uppercase">S.ft</span>
                        <p className="font-semibold">{plan.specs.sft}</p>
                      </div>
                      <div className="text-center">
                        <span className="text-white/70 text-[10px] uppercase">Type</span>
                        <p className="font-semibold">{plan.specs.type}</p>
                      </div>
                      <div className="text-center">
                        <span className="text-white/70 text-[10px] uppercase">Carpet Area</span>
                        <p className="font-semibold">{plan.specs.carpetArea}</p>
                      </div>
                      <div className="text-center">
                        <span className="text-white/70 text-[10px] uppercase">Balcony</span>
                        <p className="font-semibold">{plan.specs.balcony}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <span className="text-white/70 text-[10px] uppercase">Study</span>
                        <p className="font-semibold">{plan.specs.study}</p>
                      </div>
                      <div className="text-center">
                        <span className="text-white/70 text-[10px] uppercase">Bed Area</span>
                        <p className="font-semibold">{plan.specs.bedArea}</p>
                      </div>
                      <div className="text-center">
                        <span className="text-white/70 text-[10px] uppercase">SBD</span>
                        <p className="font-semibold">{plan.specs.sbd}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Slide Indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide ? 'bg-olive w-6' : 'bg-olive/30'
                }`}
                data-testid={`plans-indicator-${index}`}
              />
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center mt-10">
          <button
            onClick={() => openModal('floor_plan', 'Get Floor Plan Details')}
            className="bg-olive text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:bg-olive-dark hover:shadow-xl transition-all duration-300"
            data-testid="plans-cta-btn"
          >
            Get Detailed Floor Plans
          </button>
        </div>
      </div>
    </section>
  );
};

function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('brochure');
  const [modalTitle, setModalTitle] = useState('Get Project Details');

  const openModal = (type, title) => {
    setModalType(type);
    setModalTitle(title);
    setIsModalOpen(true);
  };

  const openWhatsApp = (message) => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, '_blank');
  };

  const makeCall = () => {
    window.location.href = `tel:${PHONE_NUMBER}`;
  };

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.section-animate').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-beige font-montserrat">
      <Toaster position="top-center" />
      
      {/* Premium Navbar */}
      <Navbar openModal={openModal} />

      {/* Section 1: Hero */}
      <section id="hero" className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_BG} 
            alt="Dream Homes 2 Villa" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-olive-dark/90 via-olive/80 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="max-w-2xl">
            <p className="text-beige/80 font-medium mb-4 tracking-wide uppercase text-sm animate-fade-up">Dream Homes 2 - Premium Villas</p>
            <h1 className="text-4xl md:text-6xl font-poppins font-bold text-white leading-tight mb-6 animate-fade-up animate-delay-100">
              Ab Waqt Hai<br />Apna Ghar Kehne Ka.
            </h1>
            <div className="space-y-2 text-beige/90 text-lg md:text-xl mb-8 animate-fade-up animate-delay-200">
              <p>EMI jo future banaye.</p>
              <p>Address jo permanent ho.</p>
              <p className="font-semibold text-white">Dream Homes 2 – ek nayi shuruaat.</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-up animate-delay-300">
              <button 
                onClick={() => openWhatsApp('Hi! I want complete project details for Dream Homes 2 on WhatsApp.')}
                className="bg-white text-olive font-semibold px-8 py-4 rounded-full shadow-lg hover:shadow-xl hover:bg-beige transition-all duration-300 flex items-center justify-center gap-2"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle size={20} />
                Get Full Project Details on WhatsApp
              </button>
              <button 
                onClick={() => openModal('site_visit', 'Book Site Visit')}
                className="bg-transparent border-2 border-white text-white font-semibold px-8 py-4 rounded-full hover:bg-white hover:text-olive transition-all duration-300 flex items-center justify-center gap-2"
                data-testid="hero-site-visit-btn"
              >
                <MapPin size={20} />
                Book Comfortable Site Visit
              </button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ArrowDown className="text-white/60" size={32} />
        </div>
      </section>

      {/* Section 2: The Problem */}
      <section className="py-16 md:py-24 bg-beige section-animate" data-testid="problem-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-poppins font-semibold text-olive-dark mb-12">
            Kab Tak...?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {[
              'Har mahine rent.',
              'Har saal uncertainty.',
              'Temporary address.',
              'Permanent tension.'
            ].map((text, index) => (
              <div 
                key={index} 
                className="bg-white p-6 rounded-2xl shadow-sm border border-beige-dark hover:border-olive transition-colors"
              >
                <p className="text-xl text-olive-dark font-medium">{text}</p>
              </div>
            ))}
          </div>

          <button 
            onClick={() => document.getElementById('solution').scrollIntoView({ behavior: 'smooth' })}
            className="text-olive font-semibold flex items-center gap-2 mx-auto hover:gap-4 transition-all"
            data-testid="problem-scroll-btn"
          >
            See What Could Change This <ArrowDown size={20} />
          </button>
        </div>
      </section>

      {/* Section 3: Project Intro (Solution) - Avinash Style */}
      <section id="solution" className="py-16 md:py-24 bg-beige-light section-animate" data-testid="solution-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-start">
            {/* Left: Image with Play Button */}
            <div className="relative">
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img 
                  src={VILLA_IMG} 
                  alt="Dream Homes 2 Duplex Villa" 
                  className="w-full h-[500px] md:h-[600px] object-cover"
                />
              </div>
              {/* Play Button Overlay */}
              <a 
                href="https://www.youtube.com/results?search_query=residential+villa+community+india"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 md:w-20 md:h-20 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300 group"
                data-testid="solution-play-btn"
              >
                <Play size={28} className="text-olive ml-1 group-hover:text-olive-dark" fill="#3E5B3C" />
              </a>
            </div>
            
            {/* Right: Content */}
            <div className="py-4">
              {/* Main Heading */}
              <h2 
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-olive-dark mb-8 leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Dream Homes 2
              </h2>
              
              {/* Description */}
              <p className="text-olive/80 text-base md:text-lg leading-relaxed mb-6">
                Dream Homes 2 redefines comfortable living with its thoughtfully designed 3BHK Duplex Villas, 
                located in a prime location. This premium development combines contemporary design with 
                practical living, offering a peaceful and exclusive environment for your family. 
                The spacious homes provide an elevated standard of living, featuring modern layouts 
                and quality finishes. Whether you're a young professional starting a family or looking 
                to upgrade your lifestyle, Dream Homes 2 offers smart EMI planning that makes 
                homeownership accessible and achievable.
              </p>
              
              {/* Tagline */}
              <p className="text-olive-dark font-medium text-lg mb-10">
                Dream Homes 2 – Elevating your lifestyle with comfort and opportunity.
              </p>
              
              {/* Project Specs */}
              <div className="space-y-4 mb-10">
                <div className="flex items-center gap-2">
                  <span className="text-orange-500 font-medium italic min-w-[140px]" style={{ fontFamily: "'Playfair Display', serif" }}>Type :</span>
                  <span className="text-olive-dark">3BHK Duplex Villa</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-orange-500 font-medium italic min-w-[140px]" style={{ fontFamily: "'Playfair Display', serif" }}>Residential Units :</span>
                  <span className="text-olive-dark">only 10 Villas</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-orange-500 font-medium italic min-w-[140px]" style={{ fontFamily: "'Playfair Display', serif" }}>Land Area :</span>
                  <span className="text-olive-dark">Premium Gated Community</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-orange-500 font-medium italic min-w-[140px]" style={{ fontFamily: "'Playfair Display', serif" }}>Super Built-Up Area :</span>
                  <span className="text-olive-dark">1600 Sq.ft</span>
                </div>
              </div>
              
              {/* Download Brochure Button */}
              <button 
                onClick={() => openModal('brochure', 'Download Brochure')}
                className="border-2 border-orange-500 text-olive-dark font-semibold px-10 py-4 rounded hover:bg-orange-500 hover:text-white transition-all duration-300 uppercase tracking-wider text-sm"
                data-testid="solution-brochure-btn"
              >
                Download Brochure
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Plans Section */}
      <PlansSection openModal={openModal} />

      {/* Section 5: Gallery */}
      <GallerySection />


     

     

      {/* Section 8: Location Section */}
      <section id="location" className="py-16 md:py-24 bg-beige section-animate" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-5xl font-poppins font-semibold text-olive-dark mb-4 text-center">
            At the Heart of Convenience.
          </h2>
          <p className="text-olive/60 text-center mb-12">Bhatera Ward No 1, Pump House Gali, MP</p>
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg h-[400px]">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3666.8896645!2d77.5!3d23.2!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjPCsDEyJzAwLjAiTiA3N8KwMzAnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%" 
                height="100%" 
                style={{ border: 0 }}
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Dream Homes 2 Location"
              ></iframe>
            </div>
            
            <div>
              <h3 className="font-poppins font-semibold text-xl text-olive-dark mb-6">Distance Highlights</h3>
              <div className="space-y-4 mb-8">
                {[
                  { place: 'School', time: '5 mins' },
                  { place: 'Market', time: '3 mins' },
                  { place: 'Hospital', time: '10 mins' },
                  { place: 'Main Road', time: '2 mins' }
                ].map((item, index) => (
                  <div key={index} className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm">
                    <span className="text-olive-dark font-medium">{item.place}</span>
                    <span className="text-olive font-semibold">{item.time}</span>
                  </div>
                ))}
              </div>
              
              <a 
                href={MAP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-olive text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:bg-olive-dark hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 w-full md:w-auto"
                data-testid="location-navigate-btn"
              >
                <Navigation size={20} />
                Open Location & Navigate
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section 9: Social Proof */}
      <section className="py-16 md:py-24 bg-beige-light section-animate" data-testid="social-proof-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="img-zoom rounded-2xl overflow-hidden shadow-xl order-2 md:order-1">
              <img 
                src={EVENING_IMG} 
                alt="Dream Homes 2 Evening View" 
                className="w-full h-[400px] object-cover"
              />
            </div>
            
            <div className="order-1 md:order-2">
              <h2 className="text-3xl md:text-5xl font-poppins font-semibold text-olive-dark mb-6">
                40+ Families Already Call It Home.
              </h2>
              <p className="text-lg text-olive/80 mb-8">
                Join a growing community of happy homeowners who made the right decision.
              </p>
              
              <button 
                onClick={makeCall}
                className="bg-olive text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:bg-olive-dark hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                data-testid="social-proof-call-btn"
              >
                <Phone size={20} />
                Talk to Our Project Advisor
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 10: Why Dream Homes 2 */}
      <section className="py-16 md:py-24 bg-beige section-animate" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-5xl font-poppins font-semibold text-olive-dark mb-4 text-center">
            The Right Home. The Right Decision.
          </h2>
          <p className="text-olive/60 text-center mb-12">Why families choose Dream Homes 2</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              { icon: Shield, title: 'Gated Secure Living', desc: '24/7 security for your family' },
              { icon: IndianRupee, title: 'Smart EMI Structure', desc: 'Affordable monthly payments' },
              { icon: TrendingUp, title: 'Growing Location', desc: 'Increasing property value' },
              { icon: Building, title: 'Asset Appreciation', desc: 'Your investment grows' },
              { icon: Heart, title: 'Family-Oriented Design', desc: 'Built for families' },
              { icon: CheckCircle, title: 'Practical Budgeting', desc: 'No hidden costs' }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl shadow-sm border border-beige-dark hover:border-olive hover:shadow-md transition-all duration-300 card-hover"
              >
                <div className="w-14 h-14 bg-beige rounded-full flex items-center justify-center mb-4">
                  <item.icon size={28} className="text-olive" />
                </div>
                <h3 className="font-poppins font-semibold text-lg text-olive-dark mb-2">{item.title}</h3>
                <p className="text-olive/60">{item.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <button 
              onClick={() => openWhatsApp('Hi! I want to see EMI vs Rent comparison for Dream Homes 2.')}
              className="bg-olive text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:bg-olive-dark hover:shadow-xl transition-all duration-300"
              data-testid="why-emi-btn"
            >
              See EMI vs Rent Comparison
            </button>
          </div>
        </div>
      </section>

      {/* Section 11: Construction Quality */}
      <section className="py-16 md:py-24 bg-beige-light section-animate" data-testid="construction-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-poppins font-semibold text-olive-dark mb-4">
            Built with Strength. Finished with Care.
          </h2>
          <p className="text-olive/60 mb-12">Quality construction that stands the test of time</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[
              'RCC Structure',
              'Earthquake Resistant',
              'Premium Fittings',
              'Weatherproof Finish',
              'ISI Mark Materials',
              'Expert Supervision',
              'Quality Assured',
              'Timely Delivery'
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-beige-dark"
              >
                <CheckCircle size={24} className="text-olive mx-auto mb-2" />
                <span className="text-olive-dark font-medium text-sm md:text-base">{item}</span>
              </div>
            ))}
          </div>
          
          <div>
            <button 
              onClick={() => openModal('brochure', 'Request Detailed Specifications')}
              className="bg-olive text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:bg-olive-dark hover:shadow-xl transition-all duration-300"
              data-testid="construction-specs-btn"
            >
              Request Detailed Specifications
            </button>
            <p className="text-olive/50 text-sm mt-3">No Obligation</p>
          </div>
        </div>
      </section>

      {/* Section 12: Testimonials */}
      <section className="py-16 md:py-24 bg-beige section-animate" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-5xl font-poppins font-semibold text-olive-dark mb-12 text-center">
            What Our Residents Say
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              {
                name: 'Rajesh Sharma',
                text: 'Bahut accha decision liya Dream Homes 2 mein invest karke. Family khush hai, bacche park mein khelte hain. EMI bhi affordable hai.',
                role: 'Software Engineer'
              },
              {
                name: 'Priya Gupta',
                text: 'Pehle rent dete the, ab apna ghar hai. Security bhi best hai. Sab kuch walking distance pe hai.',
                role: 'Teacher'
              },
              {
                name: 'Amit Verma',
                text: 'Construction quality top-notch hai. Builder ne jo bola wo diya. Site visit zaroor karo, dekh ke vishwas hoga.',
                role: 'Business Owner'
              }
            ].map((testimonial, index) => (
              <div 
                key={index}
                className="bg-beige-light p-8 rounded-2xl border-l-4 border-olive shadow-sm"
              >
                <div className="flex gap-1 mb-4">
                  {[1,2,3,4,5].map(star => (
                    <Star key={star} size={18} className="text-olive fill-olive" />
                  ))}
                </div>
                <p className="text-olive-dark mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-olive-dark">{testimonial.name}</p>
                  <p className="text-olive/60 text-sm">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <button 
              onClick={() => openModal('site_visit', 'See Real Site Experience')}
              className="bg-transparent border-2 border-olive text-olive font-semibold px-8 py-4 rounded-full hover:bg-olive hover:text-white transition-all duration-300"
              data-testid="testimonials-visit-btn"
            >
              See Real Site Experience
            </button>
          </div>
        </div>
      </section>

      {/* Section 13: Community + Growth */}
      <section className="py-16 md:py-24 bg-beige-light section-animate" data-testid="community-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-poppins font-semibold text-olive-dark mb-6">
                A Secure Community. A Growing Future.
              </h2>
              <p className="text-lg text-olive/80 mb-8">
                Be part of a thriving neighborhood where your investment and lifestyle both grow together.
              </p>
              
              <button 
                onClick={() => openModal('availability', 'Check Current Availability')}
                className="bg-olive text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:bg-olive-dark hover:shadow-xl transition-all duration-300"
                data-testid="community-availability-btn"
              >
                Check Current Availability
              </button>
            </div>
            
            <div className="img-zoom rounded-2xl overflow-hidden shadow-xl">
              <img 
                src={COMMUNITY_IMG} 
                alt="Dream Homes 2 Community" 
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 14: Urgency Section */}
      <section className="py-16 md:py-24 bg-olive-dark section-animate" data-testid="urgency-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-poppins font-bold text-white mb-8 uppercase tracking-wide">
            Only Limited Villas Available
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { step: '1', text: 'Book Site Visit' },
              { step: '2', text: 'Select Unit' },
              { step: '3', text: 'Lock Your Dream Home' }
            ].map((item, index) => (
              <div key={index} className="bg-white/10 p-6 rounded-2xl backdrop-blur-sm">
                <div className="w-12 h-12 bg-white text-olive-dark font-bold text-xl rounded-full flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <p className="text-white font-medium">{item.text}</p>
              </div>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => openModal('site_visit', 'BOOK SITE VISIT NOW')}
              className="bg-white text-olive-dark font-bold px-10 py-5 rounded-full shadow-lg hover:shadow-xl hover:bg-beige transition-all duration-300 text-lg uppercase"
              data-testid="urgency-visit-btn"
            >
              Book Site Visit Now
            </button>
            <button 
              onClick={() => openWhatsApp('Hi! I want to reserve a villa at Dream Homes 2.')}
              className="bg-transparent border-2 border-white text-white font-semibold px-10 py-5 rounded-full hover:bg-white hover:text-olive-dark transition-all duration-300"
              data-testid="urgency-reserve-btn"
            >
              Reserve Your Villa
            </button>
          </div>
        </div>
      </section>

      {/* Section 15: Final Conversion Block */}
      <section id="contact" className="py-16 md:py-24 bg-olive section-animate" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-poppins font-bold text-white mb-12">
            Your New Address Awaits.
          </h2>
          
          <div className="flex flex-col gap-4 max-w-md mx-auto mb-12">
            <button 
              onClick={makeCall}
              className="bg-white text-olive font-bold px-8 py-5 rounded-full shadow-lg hover:shadow-xl hover:bg-beige transition-all duration-300 flex items-center justify-center gap-3 text-lg"
              data-testid="final-call-btn"
            >
              <Phone size={24} />
              Call Now
            </button>
            <button 
              onClick={() => openWhatsApp('Hi! I want to know more about Dream Homes 2.')}
              className="bg-white text-olive font-bold px-8 py-5 rounded-full shadow-lg hover:shadow-xl hover:bg-beige transition-all duration-300 flex items-center justify-center gap-3 text-lg"
              data-testid="final-whatsapp-btn"
            >
              <MessageCircle size={24} />
              WhatsApp Now
            </button>
            <button 
              onClick={() => openModal('brochure', 'Download Brochure')}
              className="bg-transparent border-2 border-white text-white font-bold px-8 py-5 rounded-full hover:bg-white hover:text-olive transition-all duration-300 flex items-center justify-center gap-3 text-lg"
              data-testid="final-brochure-btn"
            >
              <Download size={24} />
              Download Brochure
            </button>
          </div>
          
          <div className="border-t border-white/20 pt-8">
            <p className="text-white/80 font-semibold mb-2">Dream Homes 2</p>
            <p className="text-white/60 text-sm mb-4">Bhatera Ward No 1, Pump House Gali, MP</p>
            <p className="text-white/60 text-sm">+91 99261 59666</p>
          </div>
          
          <p className="text-white/40 text-xs mt-8">
            *Terms and conditions apply. Prices and availability subject to change. RERA Registration applied.
          </p>
        </div>
      </section>

      {/* Mobile Sticky CTA Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-olive shadow-[0_-4px_20px_rgba(0,0,0,0.2)] md:hidden" data-testid="mobile-sticky-bar">
        <div className="flex justify-around py-3">
          <button 
            onClick={makeCall}
            className="flex flex-col items-center text-white text-xs font-medium"
            data-testid="mobile-call-btn"
          >
            <Phone size={22} className="mb-1" />
            Call
          </button>
          <button 
            onClick={() => openWhatsApp('Hi! I want to know more about Dream Homes 2.')}
            className="flex flex-col items-center text-white text-xs font-medium"
            data-testid="mobile-whatsapp-btn"
          >
            <MessageCircle size={22} className="mb-1" />
            WhatsApp
          </button>
          <button 
            onClick={() => openModal('site_visit', 'Book Site Visit')}
            className="flex flex-col items-center text-white text-xs font-medium"
            data-testid="mobile-visit-btn"
          >
            <MapPin size={22} className="mb-1" />
            Visit
          </button>
          <button 
            onClick={() => openModal('brochure', 'Download Brochure')}
            className="flex flex-col items-center text-white text-xs font-medium"
            data-testid="mobile-brochure-btn"
          >
            <Download size={22} className="mb-1" />
            Brochure
          </button>
        </div>
      </div>

      {/* Lead Form Modal */}
      <LeadFormModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        formType={modalType}
        title={modalTitle}
      />
    </div>
  );
}

export default App;
